/**
 * Admin → DentalStaff tab.
 * Renders inside the existing /admin AdminDashboard. Receives `adminToken` prop.
 *
 * 7 inner sub-tabs that map 1:1 to /api/admin/dentalstaff/* endpoints.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Users, Briefcase, TrendingUp, Heart, Bell, Mail, Bookmark, Loader2, RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch { return iso; }
}

async function fetchJson(adminToken, path, attempt = 0) {
  try {
    const res = await fetch(`${API}${path}`, {
      headers: { Authorization: `Bearer ${adminToken}` },
      credentials: 'omit',
      cache: 'no-store',
    });
    if (res.status === 401 && attempt < 2) {
      // React 19 strict-mode fires effects twice in dev; the dev backend
      // occasionally rejects one of two parallel requests during HMR reloads.
      // Retry once with a tiny stagger. Harmless in production.
      await new Promise((r) => setTimeout(r, 250 * (attempt + 1)));
      return fetchJson(adminToken, path, attempt + 1);
    }
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try { detail = (await res.json()).detail || detail; } catch {}
      throw new Error(detail);
    }
    return res.json();
  } catch (e) {
    if (e?.name === 'TypeError' && attempt < 2) {
      await new Promise((r) => setTimeout(r, 250 * (attempt + 1)));
      return fetchJson(adminToken, path, attempt + 1);
    }
    throw e;
  }
}

const SUBTABS = [
  { id: 'overview',     label: 'Overview',     icon: TrendingUp },
  { id: 'jobseekers',   label: 'Jobseekers',   icon: Users },
  { id: 'practices',    label: 'Per-Practice', icon: Briefcase },
  { id: 'funnel',       label: 'Funnel',       icon: TrendingUp },
  { id: 'interests',    label: 'Interest ♥',   icon: Heart },
  { id: 'alerts',       label: 'Alert Log',    icon: Bell },
  { id: 'bookmarklet',  label: 'Bookmarklet',  icon: Bookmark },
];

export default function DentalStaffAdminPanel({ adminToken }) {
  const [tab, setTab] = useState('overview');
  return (
    <div data-testid="admin-dentalstaff-panel" className="space-y-4">
      {/* Subtab nav */}
      <div className="bg-white border border-slate-200 rounded-xl p-1 flex flex-wrap gap-1">
        {SUBTABS.map((s) => {
          const Icon = s.icon;
          const on = tab === s.id;
          return (
            <button
              key={s.id}
              data-testid={`ds-tab-${s.id}`}
              onClick={() => setTab(s.id)}
              className={`px-3 py-2 text-sm font-bold rounded-lg flex items-center gap-1.5 transition-colors ${
                on ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              <Icon className="w-4 h-4" /> {s.label}
            </button>
          );
        })}
      </div>

      {tab === 'overview'    && <OverviewView    adminToken={adminToken} />}
      {tab === 'jobseekers'  && <JobseekersView  adminToken={adminToken} />}
      {tab === 'practices'   && <PracticesView   adminToken={adminToken} />}
      {tab === 'funnel'      && <FunnelView      adminToken={adminToken} />}
      {tab === 'interests'   && <InterestsView   adminToken={adminToken} />}
      {tab === 'alerts'      && <AlertsView      adminToken={adminToken} />}
      {tab === 'bookmarklet' && <BookmarkletView adminToken={adminToken} />}
    </div>
  );
}

/* ============== Shared loader wrapper ============== */
function useFetch(adminToken, path) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await fetchJson(adminToken, path);
      setData(r);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [adminToken, path]);
  useEffect(() => { load(); }, [load]);
  return { data, err, loading, reload: load };
}

function Card({ title, value, sub, accent = 'blue' }) {
  const tones = {
    blue:    'from-blue-50 to-cyan-50 border-blue-200 text-blue-900',
    emerald: 'from-emerald-50 to-teal-50 border-emerald-200 text-emerald-900',
    amber:   'from-amber-50 to-orange-50 border-amber-200 text-amber-900',
    rose:    'from-rose-50 to-pink-50 border-rose-200 text-rose-900',
    slate:   'from-slate-50 to-white border-slate-200 text-slate-900',
  };
  return (
    <div className={`bg-gradient-to-br ${tones[accent]} border-2 rounded-xl p-4`}>
      <p className="text-[11px] font-bold uppercase tracking-wider opacity-70">{title}</p>
      <p className="text-3xl font-extrabold mt-1 leading-none">{value}</p>
      {sub && <p className="text-xs mt-1 opacity-70">{sub}</p>}
    </div>
  );
}

function ErrBanner({ err, onRetry }) {
  if (!err) return null;
  return (
    <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-700 text-sm flex items-center justify-between">
      <span>{err}</span>
      {onRetry && (
        <button onClick={onRetry} className="px-3 py-1 bg-white border border-rose-300 rounded text-rose-700 font-bold text-xs flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Retry
        </button>
      )}
    </div>
  );
}

function LoadingRow({ label = 'Loading…' }) {
  return (
    <div className="py-10 text-center text-slate-500 flex items-center justify-center gap-2 text-sm">
      <Loader2 className="w-5 h-5 animate-spin" /> {label}
    </div>
  );
}

/* ============== Overview ============== */
function OverviewView({ adminToken }) {
  const { data, err, loading, reload } = useFetch(adminToken, '/api/admin/dentalstaff/overview');
  if (loading) return <LoadingRow />;
  if (err) return <ErrBanner err={err} onRetry={reload} />;
  const c = data?.counts || {};
  const js = c.jobseekers || {};
  const ap = c.applicants || {};
  const pr = c.practices || {};
  const al = c.alerts || {};
  return (
    <div data-testid="ds-overview">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500">As of {fmtDate(data?.asOf)}</p>
        <button onClick={reload} className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      <h3 className="text-sm font-extrabold text-slate-900 mb-2 uppercase tracking-wider">Jobseekers</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
        <Card title="Total"        value={js.total ?? 0}      accent="blue" />
        <Card title="Last 24h"     value={js.last24h ?? 0}    accent="emerald" sub="new signups" />
        <Card title="Last 7 days"  value={js.last7d ?? 0}     accent="emerald" />
        <Card title="With photo"   value={js.withPhoto ?? 0}  accent="amber" />
        <Card title="With resume"  value={js.withResume ?? 0} accent="amber" />
      </div>

      <h3 className="text-sm font-extrabold text-slate-900 mb-2 uppercase tracking-wider">Applicants (across all practices)</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <Card title="Total"        value={ap.total ?? 0}              accent="blue" />
        <Card title="PractCom Auto"   value={ap.dentalstaffAuto ?? 0}     accent="emerald" />
        <Card title="Interest ♥"   value={ap.dentalstaffInterest ?? 0} accent="rose" />
        <Card title="External"     value={ap.external ?? 0}            accent="amber" />
        <Card title="Last 24h"     value={ap.last24h ?? 0}             accent="emerald" />
        <Card title="Hired ✅"     value={ap.hired ?? 0}               accent="emerald" />
      </div>

      <h3 className="text-sm font-extrabold text-slate-900 mb-2 uppercase tracking-wider">Practices & Alerts</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card title="Active practices"        value={pr.active ?? 0} accent="slate" />
        <Card title="With applicants"         value={pr.engagedWithApplicants ?? 0} accent="blue" sub={`${pr.active ? Math.round(100 * (pr.engagedWithApplicants || 0) / pr.active) : 0}% engaged`} />
        <Card title="Alerts sent (total)"     value={al.total ?? 0} accent="amber" />
        <Card title="Alerts last 24h"         value={al.last24h ?? 0} accent="amber" />
      </div>
    </div>
  );
}

/* ============== Jobseekers ============== */
function JobseekersView({ adminToken }) {
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const path = `/api/admin/dentalstaff/jobseekers?limit=200${q ? `&search=${encodeURIComponent(q)}` : ''}`;
  const { data, err, loading, reload } = useFetch(adminToken, path);
  return (
    <div data-testid="ds-jobseekers">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') setQ(search); }}
          placeholder="Search by email / name / ZIP…"
          data-testid="ds-jobseekers-search"
          className="flex-1 min-w-[200px] px-3 py-2 bg-white border-2 border-slate-200 rounded-lg text-sm focus:border-blue-500 outline-none"
        />
        <button onClick={() => setQ(search)} className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold rounded-lg">Search</button>
        <button onClick={reload} className="px-3 py-2 text-slate-700 hover:bg-slate-100 rounded-lg text-sm font-bold flex items-center gap-1"><RefreshCw className="w-4 h-4" /> Refresh</button>
        <span className="text-xs text-slate-500 ml-auto">{data?.total ?? 0} total</span>
      </div>
      {loading ? <LoadingRow /> : err ? <ErrBanner err={err} onRetry={reload} /> : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">ZIP</th>
                <th className="px-3 py-2 text-left">Roles</th>
                <th className="px-3 py-2 text-left">Yrs</th>
                <th className="px-3 py-2 text-left">Photo</th>
                <th className="px-3 py-2 text-left">Resume</th>
                <th className="px-3 py-2 text-left">Signed up</th>
              </tr>
            </thead>
            <tbody>
              {(data?.jobseekers || []).map((j) => (
                <tr key={j.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-semibold">{j.firstName} {j.lastName}</td>
                  <td className="px-3 py-2 text-slate-700">{j.email}</td>
                  <td className="px-3 py-2 text-slate-700">{j.zipCode || '—'}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{(j.rolesInterested || []).slice(0, 3).join(', ') || '—'}</td>
                  <td className="px-3 py-2 text-slate-700">{j.yearsExperience ?? '—'}</td>
                  <td className="px-3 py-2">{j.photoFileId ? '✓' : '—'}</td>
                  <td className="px-3 py-2">{j.resumeFileId ? '✓' : '—'}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(j.createdAt)}</td>
                </tr>
              ))}
              {data?.jobseekers?.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">No jobseekers match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============== Per-practice activity ============== */
function PracticesView({ adminToken }) {
  const { data, err, loading, reload } = useFetch(adminToken, '/api/admin/dentalstaff/practices');
  return (
    <div data-testid="ds-practices">
      {loading ? <LoadingRow /> : err ? <ErrBanner err={err} onRetry={reload} /> : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Practice</th>
                <th className="px-3 py-2 text-left">ZIP</th>
                <th className="px-3 py-2 text-center">Total</th>
                <th className="px-3 py-2 text-center">Auto</th>
                <th className="px-3 py-2 text-center">♥</th>
                <th className="px-3 py-2 text-center">Ext.</th>
                <th className="px-3 py-2 text-center">New</th>
                <th className="px-3 py-2 text-center">Rev.</th>
                <th className="px-3 py-2 text-center">Short.</th>
                <th className="px-3 py-2 text-center">Hired</th>
                <th className="px-3 py-2 text-center">Alerts</th>
                <th className="px-3 py-2 text-left">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {(data?.practices || []).map((p) => (
                <tr key={p.practiceId} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <p className="font-semibold">{p.name}</p>
                    <p className="text-xs text-slate-500">{p.email}</p>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{p.zipCode || '—'}</td>
                  <td className="px-3 py-2 text-center font-bold">{p.applicantsTotal}</td>
                  <td className="px-3 py-2 text-center">{p.applicantsAuto}</td>
                  <td className="px-3 py-2 text-center text-rose-600">{p.applicantsInterest}</td>
                  <td className="px-3 py-2 text-center">{p.applicantsExternal}</td>
                  <td className="px-3 py-2 text-center text-blue-700">{p.statusNew}</td>
                  <td className="px-3 py-2 text-center">{p.statusReviewed}</td>
                  <td className="px-3 py-2 text-center">{p.statusShortlisted}</td>
                  <td className="px-3 py-2 text-center text-emerald-700 font-bold">{p.statusHired}</td>
                  <td className="px-3 py-2 text-center text-xs">{p.instantAlertsOn ? '✓ ON' : '✗ off'}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(p.lastActivity)}</td>
                </tr>
              ))}
              {data?.practices?.length === 0 && (
                <tr><td colSpan={12} className="px-3 py-8 text-center text-slate-400">No practice activity yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============== Funnel ============== */
function FunnelView({ adminToken }) {
  const { data, err, loading, reload } = useFetch(adminToken, '/api/admin/dentalstaff/funnel');
  if (loading) return <LoadingRow />;
  if (err) return <ErrBanner err={err} onRetry={reload} />;
  const f = data?.funnel || {};
  const cr = data?.conversionRates || {};
  const total = data?.total || 0;
  const bar = (label, n, color) => {
    const pct = total ? (100 * n / total) : 0;
    return (
      <div className="space-y-1" key={label}>
        <div className="flex justify-between text-sm">
          <span className="font-semibold text-slate-700">{label}</span>
          <span className="text-slate-600">{n} <span className="text-slate-400">({pct.toFixed(1)}%)</span></span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };
  return (
    <div data-testid="ds-funnel" className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-700 mb-3">Pipeline status — {total} total applicants</h3>
        <div className="space-y-3">
          {bar('New',         f.new || 0,         'bg-blue-500')}
          {bar('Reviewed',    f.reviewed || 0,    'bg-indigo-500')}
          {bar('Shortlisted', f.shortlisted || 0, 'bg-emerald-500')}
          {bar('Hired',       f.hired || 0,       'bg-emerald-700')}
          {bar('Rejected',    f.rejected || 0,    'bg-slate-400')}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card title="New → Reviewed+"  value={`${cr.newToReviewed ?? 0}%`} accent="blue" />
        <Card title="Reviewed → Short" value={`${cr.reviewedToShort ?? 0}%`} accent="emerald" />
        <Card title="Short → Hired"    value={`${cr.shortToHired ?? 0}%`} accent="emerald" />
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-700 mb-3">By source</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
          {Object.entries(data?.bySource || {}).map(([k, v]) => (
            <div key={k} className="p-2 border border-slate-200 rounded-lg">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">{k.replace('_', ' ')}</p>
              <p className="font-extrabold text-slate-900">{v}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============== Express-interest log ============== */
function InterestsView({ adminToken }) {
  const { data, err, loading, reload } = useFetch(adminToken, '/api/admin/dentalstaff/interests?limit=200');
  return (
    <div data-testid="ds-interests">
      {loading ? <LoadingRow /> : err ? <ErrBanner err={err} onRetry={reload} /> : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">Jobseeker</th>
                <th className="px-3 py-2 text-left">Practice</th>
                <th className="px-3 py-2 text-center">Distance</th>
                <th className="px-3 py-2 text-left">Current status</th>
              </tr>
            </thead>
            <tbody>
              {(data?.interests || []).map((i) => (
                <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(i.updatedAt)}</td>
                  <td className="px-3 py-2">
                    <p className="font-semibold">{i.jobseekerName || '—'}</p>
                    <p className="text-xs text-slate-500">{i.jobseekerEmail || ''}</p>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{i.practiceName}</td>
                  <td className="px-3 py-2 text-center text-xs">{i.distanceMiles != null ? `${i.distanceMiles} mi` : '—'}</td>
                  <td className="px-3 py-2 capitalize">{i.status}</td>
                </tr>
              ))}
              {data?.interests?.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">No express-interest events yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============== Alert log ============== */
function AlertsView({ adminToken }) {
  const { data, err, loading, reload } = useFetch(adminToken, '/api/admin/dentalstaff/alert-log?limit=200');
  return (
    <div data-testid="ds-alerts">
      {loading ? <LoadingRow /> : err ? <ErrBanner err={err} onRetry={reload} /> : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">To</th>
                <th className="px-3 py-2 text-left">Practice</th>
                <th className="px-3 py-2 text-left">Trigger</th>
                <th className="px-3 py-2 text-left">Subject</th>
                <th className="px-3 py-2 text-center">Sent</th>
              </tr>
            </thead>
            <tbody>
              {(data?.alerts || []).map((a, idx) => (
                <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(a.sentAt)}</td>
                  <td className="px-3 py-2 text-slate-700">{a.to}</td>
                  <td className="px-3 py-2 text-slate-700">{a.practiceName || '—'}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${a.trigger === 'express_interest' ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700'}`}>
                      {a.trigger === 'express_interest' ? '♥ Interest' : 'Auto match'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{a.subject}</td>
                  <td className="px-3 py-2 text-center">{a.delivered ? '✅' : '⚠️'}</td>
                </tr>
              ))}
              {data?.alerts?.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No alerts logged yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============== Bookmarklet stats ============== */
function BookmarkletView({ adminToken }) {
  const { data, err, loading, reload } = useFetch(adminToken, '/api/admin/dentalstaff/bookmarklet-stats');
  if (loading) return <LoadingRow />;
  if (err) return <ErrBanner err={err} onRetry={reload} />;
  return (
    <div data-testid="ds-bookmarklet">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
        <Card title="Total manual"   value={data?.totalManual ?? 0} accent="slate" />
        <Card title="Via bookmarklet" value={data?.viaBookmarklet ?? 0} accent="amber" sub={`${data?.bookmarkletShare ?? 0}% adoption`} />
        <Card title="Typed manually"  value={data?.typedManually ?? 0} accent="blue" />
        <Card title="Boards represented" value={(data?.topSourceLabels || []).length} accent="emerald" />
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-700 mb-3">Top sources practices found candidates on</h3>
        {(data?.topSourceLabels || []).length === 0 ? (
          <p className="text-sm text-slate-400">No manual candidates yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {(data?.topSourceLabels || []).map((s, i) => (
              <li key={i} className="flex items-center justify-between text-sm">
                <span className="font-semibold text-slate-800">{s.label}</span>
                <span className="text-slate-600">{s.count}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
